/**
 * 政策面板组件
 * 显示政策管理和发布
 */

import { useState } from 'react'
import { usePolicyStore } from '../../stores/policyStore'
import './PolicyPanel.css'

interface PolicyPanelProps {
  factionId: string
}

export function PolicyPanel({ factionId: _factionId }: PolicyPanelProps) {
  const [activeTab, setActiveTab] = useState<'policies' | 'categories' | 'events'>('policies')
  const [selectedPolicy, setSelectedPolicy] = useState<string | null>(null)
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [filterType, setFilterType] = useState<string>('all')
  
  const { 
    policies, 
    events, 
    categories,
    voteOnPolicy,
    approvePolicy,
    implementPolicy,
    repealPolicy,
    proposePolicy
  } = usePolicyStore()
  
  // 过滤政策
  const filteredPolicies = Array.from(policies.values()).filter(policy => {
    const statusMatch = filterStatus === 'all' || policy.status === filterStatus
    const typeMatch = filterType === 'all' || policy.type === filterType
    return statusMatch && typeMatch
  })
  
  // 处理投票
  const handleVote = (policyId: string, vote: 'yes' | 'no' | 'abstain') => {
    voteOnPolicy(policyId, 'char_zhou_wuwang', vote)
  }
  
  // 处理批准政策
  const handleApprove = (policyId: string) => {
    approvePolicy(policyId)
  }
  
  // 处理实施政策
  const handleImplement = (policyId: string) => {
    implementPolicy(policyId)
  }
  
  // 处理废除政策
  const handleRepeal = (policyId: string) => {
    repealPolicy(policyId)
  }
  
  // 处理创建政策
  const handleCreatePolicy = () => {
    proposePolicy(
      '新政策',
      'economic',
      '一项新的经济政策',
      [
        {
          type: 'resource_bonus',
          target: 'money',
          value: 10,
          description: '金钱收入 +10%'
        }
      ],
      {
        money: 500,
        resources: {},
        time: 15,
        approval: 50
      }
    )
  }
  
  return (
    <div className="policy-panel">
      {/* 标签页 */}
      <div className="policy-tabs">
        <button 
          className={`policy-tab ${activeTab === 'policies' ? 'active' : ''}`}
          onClick={() => setActiveTab('policies')}
        >
          政策
        </button>
        <button 
          className={`policy-tab ${activeTab === 'categories' ? 'active' : ''}`}
          onClick={() => setActiveTab('categories')}
        >
          类别
        </button>
        <button 
          className={`policy-tab ${activeTab === 'events' ? 'active' : ''}`}
          onClick={() => setActiveTab('events')}
        >
          事件
        </button>
      </div>
      
      {/* 内容区域 */}
      <div className="policy-content">
        {/* 政策管理 */}
        {activeTab === 'policies' && (
          <div className="policies-section">
            <div className="section-header">
              <h3>政策管理</h3>
              <button 
                className="create-policy-btn"
                onClick={handleCreatePolicy}
              >
                发布政策
              </button>
            </div>
            
            {/* 过滤器 */}
            <div className="policy-filters">
              <select 
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="filter-select"
              >
                <option value="all">所有状态</option>
                <option value="proposed">提议中</option>
                <option value="debating">讨论中</option>
                <option value="approved">已批准</option>
                <option value="implemented">已实施</option>
                <option value="expired">已过期</option>
                <option value="repealed">已废除</option>
              </select>
              <select 
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="filter-select"
              >
                <option value="all">所有类型</option>
                <option value="economic">经济</option>
                <option value="military">军事</option>
                <option value="cultural">文化</option>
                <option value="diplomatic">外交</option>
                <option value="internal">内政</option>
                <option value="agricultural">农业</option>
              </select>
            </div>
            
            <div className="policies-list">
              {filteredPolicies.map(policy => (
                <div 
                  key={policy.id}
                  className={`policy-card ${policy.status} ${policy.priority}`}
                  onClick={() => setSelectedPolicy(policy.id)}
                >
                  <div className="policy-header">
                    <div className="policy-info">
                      <h4>{policy.name}</h4>
                      <div className="policy-meta">
                        <span className={`policy-type ${policy.type}`}>
                          {getPolicyTypeText(policy.type)}
                        </span>
                        <span className={`policy-status ${policy.status}`}>
                          {getPolicyStatusText(policy.status)}
                        </span>
                        <span className={`policy-priority ${policy.priority}`}>
                          {getPolicyPriorityText(policy.priority)}
                        </span>
                      </div>
                    </div>
                    <div className="policy-scope">
                      {policy.scope === 'global' ? '全局' : policy.scope === 'regional' ? '区域' : '城市'}
                    </div>
                  </div>
                  
                  <div className="policy-description">
                    <p>{policy.description}</p>
                  </div>
                  
                  <div className="policy-effects">
                    <h5>效果:</h5>
                    <div className="effects-list">
                      {policy.effects.map((effect, index) => (
                        <div key={index} className="effect-item">
                          <span className="effect-icon">+</span>
                          <span className="effect-text">{effect.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="policy-cost">
                    <h5>成本:</h5>
                    <div className="costs-list">
                      <span>金钱: {policy.cost.money}</span>
                      {policy.cost.resources.food && (
                        <span>粮食: {policy.cost.resources.food}</span>
                      )}
                      {policy.cost.resources.metal && (
                        <span>金属: {policy.cost.resources.metal}</span>
                      )}
                      {policy.cost.resources.wood && (
                        <span>木材: {policy.cost.resources.wood}</span>
                      )}
                      <span>时间: {policy.cost.time} 天</span>
                    </div>
                  </div>
                  
                  {policy.status === 'proposed' && (
                    <div className="policy-voting">
                      <h5>投票:</h5>
                      <div className="voting-info">
                        <span>赞成: {policy.voting.yesVotes}</span>
                        <span>反对: {policy.voting.noVotes}</span>
                        <span>弃权: {policy.voting.abstentions}</span>
                        <span>总计: {policy.voting.totalVotes}</span>
                      </div>
                      <div className="voting-actions">
                        <button 
                          className="vote-btn yes"
                          onClick={() => handleVote(policy.id, 'yes')}
                        >
                          赞成
                        </button>
                        <button 
                          className="vote-btn no"
                          onClick={() => handleVote(policy.id, 'no')}
                        >
                          反对
                        </button>
                        <button 
                          className="vote-btn abstain"
                          onClick={() => handleVote(policy.id, 'abstain')}
                        >
                          弃权
                        </button>
                      </div>
                    </div>
                  )}
                  
                  <div className="policy-actions">
                    {policy.status === 'proposed' && (
                      <button 
                        className="action-btn approve"
                        onClick={() => handleApprove(policy.id)}
                      >
                        批准
                      </button>
                    )}
                    {policy.status === 'approved' && (
                      <button 
                        className="action-btn implement"
                        onClick={() => handleImplement(policy.id)}
                      >
                        实施
                      </button>
                    )}
                    {policy.status === 'implemented' && (
                      <button 
                        className="action-btn repeal"
                        onClick={() => handleRepeal(policy.id)}
                      >
                        废除
                      </button>
                    )}
                  </div>
                </div>
              ))}
              
              {filteredPolicies.length === 0 && (
                <div className="empty-state">
                  <span className="empty-icon">📜</span>
                  <p>暂无政策</p>
                  <button 
                    className="create-policy-btn small"
                    onClick={handleCreatePolicy}
                  >
                    发布第一个政策
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* 政策类别 */}
        {activeTab === 'categories' && (
          <div className="categories-section">
            <div className="section-header">
              <h3>政策类别</h3>
            </div>
            
            <div className="categories-grid">
              {Array.from(categories.values()).map(category => {
                const categoryPolicies = Array.from(policies.values()).filter(policy => 
                  policy.type === category.id.replace('category_', '')
                )
                
                return (
                  <div key={category.id} className="category-card">
                    <div className="category-header">
                      <span className="category-icon">{category.icon}</span>
                      <h4>{category.name}</h4>
                    </div>
                    <div className="category-description">
                      <p>{category.description}</p>
                    </div>
                    <div className="category-stats">
                      <span>政策数量: {categoryPolicies.length}</span>
                      <span>活跃政策: {categoryPolicies.filter(p => p.status === 'implemented').length}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
        
        {/* 政策事件 */}
        {activeTab === 'events' && (
          <div className="events-section">
            <div className="section-header">
              <h3>政策事件</h3>
            </div>
            
            <div className="events-list">
              {Array.from(events.values()).map(event => {
                const policy = policies.get(event.policyId)
                return (
                  <div key={event.id} className="event-card">
                    <div className="event-header">
                      <h4>{policy?.name || '未知政策'}</h4>
                      <span className="event-type">{getEventTypeText(event.type)}</span>
                    </div>
                    <div className="event-content">
                      <p>{event.description}</p>
                      <span className="event-date">{formatDate(event.date)}</span>
                    </div>
                  </div>
                )
              })}
              
              {events.size === 0 && (
                <div className="empty-state">
                  <span className="empty-icon">📅</span>
                  <p>暂无政策事件</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* 选中政策详情 */}
      {selectedPolicy && (
        <div className="policy-detail-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>政策详情</h3>
              <button 
                className="close-btn"
                onClick={() => setSelectedPolicy(null)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {/* 详细信息 */}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// 辅助函数
function getPolicyTypeText(type: string): string {
  const typeTexts = {
    economic: '经济',
    military: '军事',
    cultural: '文化',
    diplomatic: '外交',
    internal: '内政',
    technological: '科技',
    agricultural: '农业'
  }
  return typeTexts[type as keyof typeof typeTexts] || '未知类型'
}

function getPolicyStatusText(status: string): string {
  const statusTexts = {
    proposed: '提议中',
    debating: '讨论中',
    approved: '已批准',
    implemented: '已实施',
    expired: '已过期',
    repealed: '已废除'
  }
  return statusTexts[status as keyof typeof statusTexts] || '未知状态'
}

function getPolicyPriorityText(priority: string): string {
  const priorityTexts = {
    low: '低',
    medium: '中',
    high: '高',
    urgent: '紧急'
  }
  return priorityTexts[priority as keyof typeof priorityTexts] || '未知优先级'
}

function getEventTypeText(type: string): string {
  const typeTexts = {
    proposal: '提议',
    debate: '讨论',
    approval: '批准',
    implementation: '实施',
    expiry: '过期',
    repeal: '废除'
  }
  return typeTexts[type as keyof typeof typeTexts] || '未知事件'
}

function formatDate(date: { year: number; month: number; day: number }): string {
  return `${date.year}年${date.month}月${date.day}日`
}
